import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "../../api/axios";

/**
 * ResearchPublicationsContent - Detailed chronological timeline of all research publications
 * Related to the Online-CADCOM platform development
 * Fetches data dynamically from the API
 */
const ResearchPublicationsContent = () => {
    const { t, i18n } = useTranslation();
    const [selectedYear, setSelectedYear] = useState("all");
    const [selectedAuthor, setSelectedAuthor] = useState("all");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [loading, setLoading] = useState(true);
    const [publicationsData, setPublicationsData] = useState(null);
    const currentLang = i18n.language?.toUpperCase() || 'EN';

    useEffect(() => {
        const fetchPublications = async () => {
            try {
                const params = new URLSearchParams();
                if (selectedYear !== "all") params.append('year', selectedYear);
                if (selectedCategory !== "all") params.append('category', selectedCategory);
                if (selectedAuthor !== "all") params.append('author', selectedAuthor);

                const response = await axios.get(`/ContentManagement/research-publications?${params.toString()}`);
                setPublicationsData(response.data);
            } catch (error) {
                console.error('Error fetching publications:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPublications();
    }, [selectedYear, selectedCategory, selectedAuthor]);

    // Helper function to get localized text
    const getLocalizedText = (item, fieldBase) => {
        const field = `${fieldBase}${currentLang}`;
        return item[field] || item[`${fieldBase}EN`] || '';
    };

    // Helper function to get localized highlights
    const getLocalizedHighlights = (item) => {
        const field = `highlights${currentLang}`;
        return item[field] || item.highlightsEN || [];
    };

    const getCategoryBadge = (category) => {
        const badges = {
            concept: { bg: 'primary', label: t("pubCategoryConcept") || "Concept" },
            methodology: { bg: 'info', label: t("pubCategoryMethodology") || "Methodology" },
            implementation: { bg: 'success', label: t("pubCategoryImplementation") || "Implementation" },
            cloud: { bg: 'secondary', label: t("pubCategoryCloud") || "Cloud" },
            decision: { bg: 'warning', label: t("pubCategoryDecision") || "Decision" },
            expert: { bg: 'danger', label: t("pubCategoryExpert") || "Expert System" },
            journal: { bg: 'dark', label: t("pubCategoryJournal") || "Journal" },
            mcda: { bg: 'info', label: t("pubCategoryMCDA") || "MCDA" },
            pcb: { bg: 'success', label: t("pubCategoryPCB") || "PCB" },
            ai: { bg: 'danger', label: t("pubCategoryAI") || "AI" },
            domain: { bg: 'warning', label: t("pubCategoryDomain") || "Domain" },
            dynamic: { bg: 'primary', label: t("pubCategoryDynamic") || "Dynamic" },
            content: { bg: 'secondary', label: t("pubCategoryContent") || "Content" }
        };
        return badges[category?.toLowerCase()] || { bg: 'secondary', label: category || "Other" };
    };

    if (loading) {
        return (
            <div className="container px-3 text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t("loading")}</span>
                </div>
            </div>
        );
    }

    const publications = publicationsData?.publications || [];
    const availableYears = publicationsData?.availableYears || [];
    const availableAuthors = publicationsData?.availableAuthors || [];
    const availableCategories = publicationsData?.availableCategories || [];
    const totalCount = publicationsData?.totalCount || 0;

    // Group by year for timeline display
    const groupedByYear = publications.reduce((acc, pub) => {
        if (!acc[pub.year]) {
            acc[pub.year] = [];
        }
        acc[pub.year].push(pub);
        return acc;
    }, {});

    return (
        <div className="container px-3">
            {/* Hero Section */}
            <div className="row mb-4">
                <div className="col-12 text-center">
                    <div className="py-4">
                        <h1 style={{ color: '#112d4e', fontWeight: 700, marginBottom: '16px' }}>
                            <i className="bi bi-journal-text me-3"></i>
                            {t("researchPublicationsTitle")}
                        </h1>
                        <p className="lead text-muted" style={{ maxWidth: '800px', margin: '0 auto' }}>
                            {t("researchPublicationsSubtitle")}
                        </p>
                    </div>
                </div>
            </div>

            {/* Back to About link */}
            <div className="row mb-4">
                <div className="col-12">
                    <Link to="/About" className="btn btn-outline-secondary">
                        <i className="bi bi-arrow-left me-2"></i>
                        {t("backToAbout")}
                    </Link>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-4">
                <div className="col-lg-10 mx-auto">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <i className="bi bi-calendar3 me-2"></i>
                                        {t("filterByYear")}
                                    </label>
                                    <select
                                        className="form-select"
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(e.target.value)}
                                    >
                                        <option value="all">{t("allYears")}</option>
                                        {availableYears.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <i className="bi bi-person me-2"></i>
                                        {t("filterByAuthor")}
                                    </label>
                                    <select
                                        className="form-select"
                                        value={selectedAuthor}
                                        onChange={(e) => setSelectedAuthor(e.target.value)}
                                    >
                                        <option value="all">{t("allAuthors")}</option>
                                        {availableAuthors.map(author => (
                                            <option key={author} value={author}>{author}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <i className="bi bi-tag me-2"></i>
                                        {t("filterByCategory")}
                                    </label>
                                    <select
                                        className="form-select"
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        <option value="all">{t("allCategories")}</option>
                                        {availableCategories.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="mt-3 text-center text-muted">
                                {t("showingPublications", { count: totalCount, total: availableYears.length > 0 ? publications.length : 0 }) || `Showing ${totalCount} publications`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistics */}
            <div className="row mb-5">
                <div className="col-lg-10 mx-auto">
                    <div className="row g-3">
                        <div className="col-6 col-md-3">
                            <div className="card border-0 bg-primary text-white text-center p-3">
                                <h2 className="mb-0">{totalCount}</h2>
                                <small>{t("totalPublications")}</small>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="card border-0 bg-success text-white text-center p-3">
                                <h2 className="mb-0">{availableYears.length}</h2>
                                <small>{t("yearsOfResearch")}</small>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="card border-0 bg-info text-white text-center p-3">
                                <h2 className="mb-0">{availableAuthors.length}</h2>
                                <small>{t("totalAuthors")}</small>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="card border-0 bg-warning text-dark text-center p-3">
                                <h2 className="mb-0">{availableCategories.length}</h2>
                                <small>{t("totalCategories") || "Categories"}</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeline */}
            <div className="row">
                <div className="col-lg-10 mx-auto">
                    {Object.keys(groupedByYear).sort((a, b) => b - a).map(year => (
                        <div key={year} className="mb-5">
                            {/* Year Header */}
                            <div className="d-flex align-items-center mb-4">
                                <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        backgroundColor: '#112d4e',
                                        color: '#fff',
                                        fontSize: '1.2rem',
                                        fontWeight: 'bold'
                                    }}>
                                    {year}
                                </div>
                                <div>
                                    <h4 className="mb-0" style={{ color: '#112d4e' }}>
                                        {groupedByYear[year].length} {groupedByYear[year].length === 1 ? t("publication") : t("publications")}
                                    </h4>
                                </div>
                            </div>

                            {/* Publications for this year */}
                            <div className="ps-4 border-start border-3" style={{ borderColor: '#112d4e !important' }}>
                                {groupedByYear[year].map((pub, idx) => {
                                    const badge = getCategoryBadge(pub.category);
                                    const highlights = getLocalizedHighlights(pub);
                                    const description = getLocalizedText(pub, 'description');
                                    const venue = pub.venue || pub.venueDate;

                                    return (
                                        <div key={pub.id} className={`card border-0 shadow-sm ${idx < groupedByYear[year].length - 1 ? 'mb-4' : ''}`}>
                                            <div className="card-body p-4">
                                                <div className="d-flex flex-wrap align-items-start justify-content-between mb-3">
                                                    <span className={`badge bg-${badge.bg} mb-2`}>{badge.label}</span>
                                                    <small className="text-muted">{venue}</small>
                                                </div>

                                                <h5 style={{ color: '#112d4e', fontWeight: 600, lineHeight: 1.4 }}>
                                                    {pub.title}
                                                </h5>

                                                <div className="mb-3">
                                                    <i className="bi bi-people me-2 text-muted"></i>
                                                    <span className="text-secondary">
                                                        {pub.authors?.join(", ")}
                                                    </span>
                                                </div>

                                                {pub.doi && (
                                                    <div className="mb-3">
                                                        <i className="bi bi-link-45deg me-2 text-muted"></i>
                                                        <a href={`https://doi.org/${pub.doi}`} target="_blank" rel="noopener noreferrer" className="text-primary">
                                                            DOI: {pub.doi}
                                                        </a>
                                                    </div>
                                                )}

                                                {pub.url && (
                                                    <div className="mb-3">
                                                        <i className="bi bi-globe me-2 text-muted"></i>
                                                        <a href={pub.url} target="_blank" rel="noopener noreferrer" className="text-primary">
                                                            {pub.url}
                                                        </a>
                                                    </div>
                                                )}

                                                {description && (
                                                    <p className="text-muted mb-3">
                                                        {description}
                                                    </p>
                                                )}

                                                {highlights && highlights.length > 0 && (
                                                    <div className="bg-light rounded p-3">
                                                        <small className="text-uppercase fw-bold text-muted d-block mb-2">
                                                            <i className="bi bi-lightbulb me-2"></i>
                                                            {t("keyContributions")}
                                                        </small>
                                                        <ul className="mb-0 ps-3">
                                                            {highlights.map((highlight, hIdx) => (
                                                                <li key={hIdx} className="small text-secondary">
                                                                    {highlight}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {publications.length === 0 && (
                        <div className="text-center py-5">
                            <i className="bi bi-journal-x" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
                            <h4 className="mt-3 text-muted">{t("noPublicationsFound") || "No publications found"}</h4>
                            <p className="text-muted">{t("tryDifferentFilters") || "Try adjusting your filters"}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Summary Section */}
            <div className="row mt-5 mb-5">
                <div className="col-lg-10 mx-auto">
                    <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="card-body p-4 p-lg-5">
                            <h3 className="text-center mb-4" style={{ color: '#112d4e', fontWeight: 600 }}>
                                <i className="bi bi-graph-up me-2"></i>
                                {t("researchEvolutionTitle")}
                            </h3>

                            <div className="row g-4">
                                <div className="col-md-4">
                                    <div className="text-center p-3">
                                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                            style={{ width: '50px', height: '50px', backgroundColor: '#e8f4fd' }}>
                                            <i className="bi bi-1-circle-fill" style={{ fontSize: '1.5rem', color: '#112d4e' }}></i>
                                        </div>
                                        <h6 style={{ color: '#112d4e' }}>{t("era1Title")}</h6>
                                        <small className="text-muted">{t("era1Desc")}</small>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="text-center p-3">
                                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                            style={{ width: '50px', height: '50px', backgroundColor: '#e8f5e9' }}>
                                            <i className="bi bi-2-circle-fill" style={{ fontSize: '1.5rem', color: '#2e7d32' }}></i>
                                        </div>
                                        <h6 style={{ color: '#112d4e' }}>{t("era2Title")}</h6>
                                        <small className="text-muted">{t("era2Desc")}</small>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="text-center p-3">
                                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                            style={{ width: '50px', height: '50px', backgroundColor: '#fff3e0' }}>
                                            <i className="bi bi-3-circle-fill" style={{ fontSize: '1.5rem', color: '#e65100' }}></i>
                                        </div>
                                        <h6 style={{ color: '#112d4e' }}>{t("era3Title")}</h6>
                                        <small className="text-muted">{t("era3Desc")}</small>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="text-center p-3">
                                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                            style={{ width: '50px', height: '50px', backgroundColor: '#fce4ec' }}>
                                            <i className="bi bi-4-circle-fill" style={{ fontSize: '1.5rem', color: '#c2185b' }}></i>
                                        </div>
                                        <h6 style={{ color: '#112d4e' }}>{t("era4Title")}</h6>
                                        <small className="text-muted">{t("era4Desc")}</small>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="text-center p-3">
                                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                            style={{ width: '50px', height: '50px', backgroundColor: '#ede7f6' }}>
                                            <i className="bi bi-5-circle-fill" style={{ fontSize: '1.5rem', color: '#7b1fa2' }}></i>
                                        </div>
                                        <h6 style={{ color: '#112d4e' }}>{t("era5Title")}</h6>
                                        <small className="text-muted">{t("era5Desc")}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResearchPublicationsContent;
